import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { DocumentFolder } from "@/features/documents/types";

const ROOT_VALUE = "__root__";

export interface FolderOption {
  id: string;
  label: string;
}

/** Flatten the folder tree to depth-labelled options ("Parent / Child"). */
export function buildFolderOptions(folders: DocumentFolder[]): FolderOption[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const pathOf = (folder: DocumentFolder): string => {
    const parts = [folder.name];
    let current = folder.parent ? byId.get(folder.parent) : undefined;
    while (current) {
      parts.unshift(current.name);
      current = current.parent ? byId.get(current.parent) : undefined;
    }
    return parts.join(" / ");
  };
  return folders
    .map((f) => ({ id: f.id, label: pathOf(f) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface FolderSelectProps {
  folders: DocumentFolder[];
  value: string | null;
  onChange: (folderId: string | null) => void;
  /** Label for the root ("no folder") option. */
  rootLabel?: string;
  id?: string;
}

/** A Select over the client's folders, with a root ("All Documents") entry. */
export function FolderSelect({
  folders,
  value,
  onChange,
  rootLabel = "All Documents (root)",
  id,
}: FolderSelectProps) {
  const options = buildFolderOptions(folders);
  return (
    <Select
      value={value ?? ROOT_VALUE}
      onValueChange={(v) => onChange(v === ROOT_VALUE ? null : v)}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ROOT_VALUE}>{rootLabel}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.id} value={opt.id}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
